import { useSpring, animated } from '@react-spring/web';
import { Input } from 'antd';
import cls from 'classnames';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import styles from './index.module.scss';
import { aiWriterIcon, aiWriterWhite } from '../../../assets';
import AddPromptIcon from '../../../assets/ImageCreation/add-prompt.svg';
import PromptTipsIcon from '../../../assets/ImageCreation/prompt-tips.svg';
import { useAppDispatch, useAppSelector } from '../../../state';
import {
  genAIWriterPrompt,
  generatePreviewImage,
  setImgConfig,
  setIsGenerating,
  addToPollQueue,
  getPromptTips,
} from '../../../state/image';
import { track } from '../../../track';
import { IPromptTips, IPromptTipsTag } from '../../../types/meta';
import { getCharLength } from '../../../utils/text';
import { LocalImage } from '../../Image';

const { TextArea } = Input;

const MAX_PROMPT_LENGTH = 3000; // 3000 个字符（1500 个汉字）

export const PromptInput: React.FC<{ source: string }> = ({ source }) => {
  const dispatch = useAppDispatch();
  const {
    imgConfig: { prompt, web_preview_extra, avatar_style_id_list },
    isGenerating,
    promptTips,
    modelType,
  } = useAppSelector((state) => state.image);
  const [inputValue, setInputValue] = useState(prompt || '');
  const [showPromptTips, setShowPromptTips] = useState(false);
  const [selectedFirstLevel, setSelectedFirstLevel] = useState<IPromptTips>(promptTips[0]);
  const [selectedSecondLevel, setSelectedSecondLevel] = useState<IPromptTipsTag | undefined>();
  const { t } = useTranslation();

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const charLength = getCharLength(value);
    if (charLength > MAX_PROMPT_LENGTH) {
      toast.error(`${t('prompt_length_error', { maxLength: MAX_PROMPT_LENGTH })}`);
      return;
    }
    setInputValue(value);
  };

  const handleInputBlur = () => {
    dispatch(setImgConfig({ prompt: inputValue }));
  };

  const handleAIWriter = () => {
    dispatch(setIsGenerating(true));
    dispatch(genAIWriterPrompt());
  };

  const handleGenerate = () => {
    track.send('generate_image_click', source, {
      image_model_id: modelType,
      style_reference_image_url: web_preview_extra?.style_img_url,
      face_reference_image_url: web_preview_extra?.face_img_url,
      style_reference_id: avatar_style_id_list?.[0],
      style_reference_intensity: web_preview_extra?.style_weight,
      face_reference_intensity: web_preview_extra?.face_weight,
      cfg_scale_value: web_preview_extra?.cfg_scale,
      image_prompt: inputValue,
    });
    dispatch(setIsGenerating(true));
    setShowPromptTips(false);
    dispatch(generatePreviewImage()).then((action) => {
      if (generatePreviewImage.fulfilled.match(action)) {
        const imgIds = action.payload.imgList
          ?.map((img: any) => img.img_id)
          .filter(Boolean) as number[];
        dispatch(addToPollQueue(imgIds));
      } else {
        const msg = (action.payload as any).status_msg || '';
        if (msg) toast.error(msg);
      }
    });
  };

  const handleSelectTipFirstLevel = (tip: IPromptTips) => {
    setSelectedFirstLevel(tip);
    if (tip.type === 'prompt' && tip.name !== selectedFirstLevel.name) {
      setSelectedSecondLevel(tip.tags[0]);
    }
  };

  const handleSelectTipSecondLevel = (tag: IPromptTipsTag) => {
    setSelectedSecondLevel(tag);
  };

  const handleSelectPrompt = (prompt: string) => {
    const newPrompt = inputValue ? `${inputValue},${prompt}` : prompt;
    if (newPrompt.length > MAX_PROMPT_LENGTH) {
      toast.error(`${t('prompt_length_error', { maxLength: MAX_PROMPT_LENGTH })}`);
      return;
    }
    setInputValue(newPrompt);
    dispatch(setImgConfig({ prompt: newPrompt }));
    track.collect('prompt_dictionary_choice_click', source, {
      primary_dictionary_tab: selectedFirstLevel.name,
      secondary_dictionary_tab: selectedSecondLevel?.name || '',
      word_choice: prompt,
    });
  };

  const handleInputFocus = () => {
    track.send('image_prompt_input_click', source);
  };

  useEffect(() => {
    dispatch(getPromptTips());
  }, []);

  useEffect(() => {
    prompt && setInputValue(prompt);
  }, [prompt]);

  const style = useSpring({
    opacity: showPromptTips ? 1 : 0,
    y: showPromptTips ? 0 : -10,
    config: { duration: 200 },
  });

  const handlePromptTipsClick = () => {
    track.send('prompt_dictionary_icon_click', source, {
      prompt_dictionary_icon_click_type: showPromptTips ? 'close' : 'open',
    });

    setShowPromptTips(!showPromptTips);
  };

  const router = useRouter();

  useEffect(() => {
    if (router.query.action === 'recreate') {
      handleGenerate();
    }
  }, [router.query.action]);

  return (
    <div className={styles.promptInputContainer}>
      <div className={styles.promptHeader}>
        <p className={styles.title}>{t('image_prompt')}</p>
        <button className={styles.aiWriter} disabled={isGenerating} onClick={handleAIWriter}>
          <LocalImage className={styles.aiWriterIcon} src={aiWriterWhite} alt="ai writer icon" />
          {t('image_ai_writer')}
        </button>
      </div>
      {/* prompt 输入框 */}
      <div className={styles.promptInput}>
        <TextArea
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          placeholder={t('image_prompt_placeholder')}
          autoSize={{ minRows: 3, maxRows: 3 }}
          className={styles.textarea}
        />
        <div className={styles.inputFooter}>
          <button className={styles.promptTipsBtn} onClick={handlePromptTipsClick}>
            <img src={PromptTipsIcon.src} alt="prompt tips icon" />
          </button>
          <button
            className={styles.generateBtn}
            disabled={!inputValue.trim() || isGenerating}
            onClick={handleGenerate}
          >
            {t('image_generate')}
          </button>
        </div>
      </div>
      {/* prompt 提示词面板 */}
      {showPromptTips && (
        <animated.div className={styles.promptTips} style={style}>
          {/* 提示词一级 */}
          <div className={styles.firstLevel}>
            {promptTips.map((tip) => (
              <>
                <div
                  className={cls(styles.firstLevelItem, {
                    [styles.active]: tip.name === selectedFirstLevel.name,
                  })}
                  onClick={() => handleSelectTipFirstLevel(tip)}
                >
                  {tip.name}
                </div>
                {tip.type === 'template' && <div className={styles.borderRight} />}
              </>
            ))}
          </div>
          <div className={styles.tipList}>
            {selectedFirstLevel.type === 'template' ? (
              // 模板提示词
              <div className={styles.templateTips}>
                {selectedFirstLevel.tags.map((tag) => (
                  <div
                    className={styles.templateTipsItem}
                    onClick={() => handleSelectPrompt(tag.prompt || '')}
                  >
                    <img src={tag.img_url} alt="template tips image" />
                    {tag.name}
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.promptTipsList}>
                {/* 提示词二级 */}
                <div className={styles.secondLevel}>
                  {selectedFirstLevel.tags.map((tag) => (
                    <div
                      className={cls(styles.secondLevelItem, {
                        [styles.active]: tag.name === selectedSecondLevel?.name,
                      })}
                      onClick={() => handleSelectTipSecondLevel(tag)}
                    >
                      {tag.name}
                    </div>
                  ))}
                </div>
                {/* 提示词三级 */}
                <div className={styles.thirdLevel}>
                  {selectedSecondLevel?.tips?.map((prompt) => (
                    <div
                      className={styles.thirdLevelItem}
                      onClick={() => handleSelectPrompt(prompt)}
                    >
                      <img src={AddPromptIcon.src} alt="add prompt icon" />
                      {prompt}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </animated.div>
      )}
    </div>
  );
};
